import { Link } from 'react-router-dom';
import { CalendarDays } from 'lucide-react';
import { isSingleEventActivity, type DashboardSession } from 'shared';
import { TypeChip, UniversoChip } from '@/components/activities/ActivityUi';
import {
  formatSessionTimes,
  sessionPhase,
  spansDays,
  type SessionPhase,
} from '@/lib/activityTime';

/**
 * The hours of a session today. An open day over several days has no hours
 * that are today's alone, so it says when it ends.
 */
function hoursToday(session: DashboardSession): string {
  if (!spansDays(session.startsAt, session.endsAt)) {
    return formatSessionTimes(session.startsAt, session.endsAt);
  }
  const lastDay = new Date(session.endsAt).toLocaleDateString('pt-PT', {
    day: '2-digit',
    month: '2-digit',
  });
  return `Até ${lastDay}`;
}

/** Where a session is run from. An open day is its own page, not a session of one. */
function sessionPath(session: DashboardSession): string {
  return isSingleEventActivity(session.activityType)
    ? `/atividades/${session.activityId}`
    : `/atividades/sessoes/${session.id}`;
}
import { formatCount } from '@/lib/reportFormat';

const PHASE_LABELS: Record<SessionPhase, string> = {
  upcoming: 'Por começar',
  open: 'A decorrer',
  over: 'Terminada',
};

function peopleAt(session: DashboardSession): string {
  const present = `${formatCount(session.attendanceCount)} ${session.attendanceCount === 1 ? 'presença' : 'presenças'}`;
  if (session.visitors === 0) return present;
  return `${present} · ${formatCount(session.visitors)} ${session.visitors === 1 ? 'visitante' : 'visitantes'}`;
}

/** The activity sessions of today, in the order they happen. */
export default function TodaySessions({ sessions }: { sessions: DashboardSession[] }) {
  return (
    <section className="card" aria-labelledby="today-sessions">
      <div className="px-5 py-4 border-b border-border flex items-center justify-between gap-3">
        <h2 id="today-sessions" className="text-sm font-semibold text-foreground flex items-center gap-2">
          <CalendarDays className="w-4 h-4 text-primary" aria-hidden="true" />
          Atividades de hoje
        </h2>
        <Link to="/atividades" className="text-xs font-medium text-primary hover:underline">
          Todas as atividades
        </Link>
      </div>

      {sessions.length === 0 ? (
        <p className="px-5 py-8 text-center text-sm text-muted-foreground">Nenhuma atividade marcada para hoje.</p>
      ) : (
        <ul className="divide-y divide-border">
          {sessions.map((s) => {
            const phase = sessionPhase(s.startsAt, s.endsAt);
            return (
              <li key={s.id}>
                <Link
                  to={sessionPath(s)}
                  className="flex items-center gap-4 px-5 py-3 hover:bg-accent/30 transition-colors"
                >
                  <span className="w-24 shrink-0 text-sm font-medium text-foreground tabular-nums">
                    {hoursToday(s)}
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm font-medium text-foreground truncate">{s.activityName}</span>
                      <TypeChip type={s.activityType} />
                      {s.universo && <UniversoChip universo={s.universo} />}
                    </span>
                    <span className="block text-xs text-muted-foreground mt-0.5">
                      {s.leaderName} · {peopleAt(s)}
                    </span>
                  </span>
                  <span
                    className={`shrink-0 text-xs font-medium ${
                      phase === 'open' ? 'text-foreground' : 'text-muted-foreground'
                    }`}
                  >
                    {PHASE_LABELS[phase]}
                  </span>
                </Link>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}
